// Copyright (c) Docugami, Inc. All rights reserved.

import { DgButton, DgFlexColumn, DgFlexRow, DgHorizontalSpacer, DgVerticalSpacer } from "components/common/DgCommonStyledComponents";
import { useDocumentDgmlContext } from "hooks/documentDgml";
import { ChoiceGroup, Dropdown, IChoiceGroupOption, IDropdownOption, Panel, PanelType, Separator } from "@fluentui/react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AppState, store } from "store";
import { UserInfo } from "../../components/common/Fonts"
import { FileManagerDataApi } from "networking/api";
import { DgmlDom } from "utils/dgmldom";
import { useFeatureFlag } from "hooks/FeatureFlags";
import { FeatureFlags } from "store/featureFlags/types";
import styled from "styled-components";
import { getDocumentById } from "store/state_helpers";
import { DocumentCommandBar } from "components/file_management/presentation/DocumentCommandBar";
import { useDownloadXmlCommand } from "hooks/DownloadXmlCommand";
import { useDownloadDgmlxCommand } from "hooks/DownloadDgmlxCommand";
import { useDownloadDocumentSplitsCommand } from "hooks/DownloadDocumentSplitsCommand";
import { useBugReportCommand } from "hooks/BugReportCommand";
import { BugReportPanel } from 'hooks/diagnostics/BugReportPanel';
import { createFolderId, isUnassigned } from "components/file_management/file_helpers";
import { dgmlSlice, useGetDgmlQuery } from "store/dgml/dgmlSlice";
import { BugType } from "store/frontend/frontendSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useAppSelector } from "hooks/redux";
import { domSelector } from "store/dgml/dgmlSelectors";
import { selectSelectedChunkInstance } from "store/sdvSelectors";

enum DiagnosticsPane {
    SelectedChunkInstance = "SelectedChunkInstance",
    SelectedChunkCpml = "SelectedChunkCpml",
    SelectedChunkTokens = "SelectedChunkTokens",
    StartText = "StartText",
    Dgml = "Dgml",
    AllTokens = "AllTokens",
}

const debugOptions: IChoiceGroupOption[] = [
    { key: DiagnosticsPane.SelectedChunkInstance, text: 'Selected ChunkInstance' },
    { key: DiagnosticsPane.SelectedChunkCpml, text: 'Selected Chunk CPML' },
    { key: DiagnosticsPane.SelectedChunkTokens, text: 'Selected Chunk Tokens' },
    { key: DiagnosticsPane.StartText, text: 'Start Text' },
    { key: DiagnosticsPane.Dgml, text: 'Dgml' },
    { key: DiagnosticsPane.AllTokens, text: 'All Tokens' },
];

const DiagnosticsPaneArea = styled.div`
    white-space: pre;
    flex: 1;
    height: 100%;
    overflow: auto;
`;

const bugTypeOptions: IDropdownOption[] = [
    { key: BugType.Table, text: 'Table' },
    { key: BugType.Form, text: 'Form' },
    { key: BugType.VLA, text: 'VLA' },
];

export function useDocumentDiagnostics(folderId: string, clickTarget: HTMLElement | undefined, filePath: string = ""): JSX.Element | null {
    const [showModal, setShowModal] = useState(false);
    const diagnosticsFeatureFlag = useFeatureFlag(FeatureFlags.DiagnosticLoggingEnabled);

    const onDoubleClick = useCallback((ev: MouseEvent) => {
        if (ev.shiftKey && diagnosticsFeatureFlag && !isUnassigned(folderId)) {
            ev.preventDefault();
            setShowModal(true);
        }
    }, [diagnosticsFeatureFlag, folderId]);

    useEffect(() => {
        const callback = (ev: MouseEvent) => {
            onDoubleClick(ev);
        }

        if (!!clickTarget) {
            clickTarget.addEventListener("dblclick", callback);
            return () => clickTarget.removeEventListener("dblclick", callback);
        }
    },
    [clickTarget, onDoubleClick])

    return showModal
        ? <DiagnosticsView folderId={folderId} filePath={filePath} onDismiss={() => setShowModal(false)} />
        : null;
}

interface DiagnosticsViewProps {
    folderId: string
    filePath: string
    onDismiss: () => void
}

const DiagnosticsView = (props: DiagnosticsViewProps): JSX.Element => {
    const [selectedPane, setSelectedPane] = useState<DiagnosticsPane>(DiagnosticsPane.SelectedChunkInstance);
    const [startText, setStartText] = useState<string>("");
    const [fetchStartText, setFetchStartText] = useState(true);
    const [selectedBugReportTypes, setSelectedBugReportTypes] = useState<BugType[]>([]);
    const folderId = createFolderId(props.folderId);
    const clusterId = folderId.clusterId;
    const fileId = folderId.fileId;

    const organizationId = useSelector((state: AppState) => state.organizations.currentOrganization.id);
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);
    const document = useSelector((state: AppState) => getDocumentById(state, props.folderId));
    const dgml = useDocumentDgmlContext(!!document?.clusterDocument?.hasCpml ? props.folderId : '');
    const pageNum = useAppSelector(state => state.sdv.document?.currentPageOnScreen !== undefined ? state.sdv.document.currentPageOnScreen + 1 : undefined);

    const selectedItems = useMemo(() => {
        if (!!props.filePath) {
            return [props.filePath]
        }
        else if (!!document?.clusterDocument.fileId) {
            return [document.clusterDocument.fileId]
        }

        return [];
    }, [props.filePath, document]);

    const downloadXmlCommand = useDownloadXmlCommand(selectedItems, () => {});
    const downloadDgmlxCommand = useDownloadDgmlxCommand(selectedItems);
    const downloadDocumentSplitsCommand = useDownloadDocumentSplitsCommand(selectedItems);
    const commands = useMemo(() => [downloadXmlCommand, downloadDgmlxCommand, downloadDocumentSplitsCommand], [downloadXmlCommand, downloadDgmlxCommand, downloadDocumentSplitsCommand]);

    const bugReportCommand = useBugReportCommand(clusterId, fileId, selectedBugReportTypes, pageNum);
    const bugReportingCommands = useMemo(() => [bugReportCommand], [bugReportCommand]);

    const { isFetching } = useGetDgmlQuery(selectedPane === DiagnosticsPane.Dgml || selectedPane === DiagnosticsPane.SelectedChunkCpml ? { docsetId: clusterId, fileId: folderId.fileId } : skipToken);
    const dom = useAppSelector(state => domSelector(state, folderId.clusterId, folderId.fileId));

    const refetchDgml = () => {
        store.dispatch(dgmlSlice.endpoints.getDgmlNodes.initiate({ docsetId: clusterId, fileId: folderId.fileId }, {forceRefetch: true}))
    }

    const onChange = useCallback((ev: FormEvent<HTMLElement | HTMLInputElement> | undefined, option: IChoiceGroupOption | undefined) => {
        if (!!option) {
            setSelectedPane(option.key as DiagnosticsPane);
        }
    }, []);

    const onBugTypeSelectionChange = (event: FormEvent<HTMLDivElement> | undefined, option: IDropdownOption | undefined): void => {
        if (option) {
            setSelectedBugReportTypes(
                option.selected ? [...selectedBugReportTypes, option.key as BugType] : selectedBugReportTypes.filter(key => key !== option.key),
            );
        }
    };

    async function ensureStartText() {
        if (!fetchStartText || !props.folderId) {
            return;
        }

        setFetchStartText(false);
        const api = new FileManagerDataApi();
        const response = await api.fileManagerGetStartText(props.folderId);
        setStartText(response.data as string);
    }

    function diagnosticsPaneContent(): string {
        switch (selectedPane) {
            case (DiagnosticsPane.SelectedChunkCpml): {
                if (!dgml.isInitialized() || !selectedChunkInstance?.spanId || !dom) {
                    return "";
                }

                return DgmlDom.getNodeBySpanId(selectedChunkInstance?.spanId, dom.root)?.toString() ?? ''
            }

            case (DiagnosticsPane.SelectedChunkTokens): {
                const selChunk = selectedChunkInstance;
                if (!selChunk || !dgml.isInitialized()) {
                    return "";
                }

                return dgml.getTokensTextParser().toString(selChunk.tokenRange!.fromTok, selChunk.tokenRange!.toTok);
            }

            case (DiagnosticsPane.SelectedChunkInstance): {
                const selChunk = selectedChunkInstance;
                if (!selChunk || !dgml.isInitialized) {
                    return "";
                }

                return JSON.stringify(selChunk, null, 2);
            }

            case (DiagnosticsPane.AllTokens): {
                if (!dgml.isInitialized) {
                    return "";
                }

                return dgml.getTokensTextParser().toString();
            }

            case (DiagnosticsPane.StartText): {
                // fire and forget this async call.
                ensureStartText();
                return startText;
            }

            case (DiagnosticsPane.Dgml): {
                if (!dgml.isInitialized() || !dom) {
                    return "";
                }

                return dom.root.toString();
            }
        }
    }

    return (
        <Panel
            isOpen={true}
            onDismiss={props.onDismiss}
            type={PanelType.extraLarge}>
            <DgFlexColumn style={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <DgVerticalSpacer height="10px" />
                <DocumentCommandBar commands={commands} />
                <Separator />
                <Dropdown
                    placeholder="Select Bug Type"
                    multiSelect
                    options={bugTypeOptions}
                    onChange={onBugTypeSelectionChange}
                    styles={{
                        dropdown: {
                            width: 180,
                            paddingBottom: 15,
                            ':focus::after': {
                                border: 'none'
                            },
                            'margin-left': 'auto'
                        }
                    }}
                />
                <DocumentCommandBar commands={bugReportingCommands} />
                <BugReportPanel
                    docsetId={clusterId}
                    fileId={fileId}
                    pageNum={pageNum}
                    types={selectedBugReportTypes} />
                <Separator />
                <UserInfo>OrgId: {organizationId}</UserInfo>
                <UserInfo>FolderId: {props.folderId}</UserInfo>
                <UserInfo>ClusterId: {clusterId}</UserInfo>
                <RefreshDgmlButton onClick={refetchDgml} disabled={isFetching}>Refresh DGML</RefreshDgmlButton>
                <Separator />
                <DgFlexRow>
                    {dgml.isInitialized() ? <ChoiceGroup selectedKey={selectedPane} options={debugOptions} onChange={onChange} /> : null}
                    <DgHorizontalSpacer width="10px" />
                    <DiagnosticsPaneArea>{diagnosticsPaneContent()}</DiagnosticsPaneArea>
                </DgFlexRow>
            </DgFlexColumn>
        </Panel>
    )
}

const RefreshDgmlButton = styled(DgButton)`
    width: fit-content;
    margin-top: 10px;
`
